import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlugZap, ShieldCheck } from "lucide-react";
import { API } from "@/context/AuthContext";
import { FinancierSettings } from "@/components/FinancierSettings";

export default function Settings() {
  const [tenantUrl, setTenantUrl] = useState("");
  const [commUser, setCommUser] = useState("");
  const [commPass, setCommPass] = useState("");
  const [homeState, setHomeState] = useState("06");
  const [msmeField, setMsmeField] = useState("");
  const [ledgerFromDate, setLedgerFromDate] = useState("2025-04-01");
  const [inventoryFromDate, setInventoryFromDate] = useState("2026-04-01");
  const [includeSettled, setIncludeSettled] = useState(true);
  const [status, setStatus] = useState("demo");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testMessage, setTestMessage] = useState("");
  const [logs, setLogs] = useState([]);
  const [syncRunning, setSyncRunning] = useState(false);

  const loadLogs = () => axios.get(`${API}/sap/sync-logs`).then((r) => setLogs(r.data)).catch(() => {});

  useEffect(() => {
    loadLogs();
    const onSynced = () => loadLogs();
    window.addEventListener("sap-synced", onSynced);
    const poll = setInterval(() => {
      axios.get(`${API}/sap/sync-status`).then((r) => {
        setSyncRunning(r.data.running);
        if (!r.data.running) loadLogs();
      }).catch(() => {});
    }, 5000);
    return () => { window.removeEventListener("sap-synced", onSynced); clearInterval(poll); };
  }, []);

  useEffect(() => {
    axios.get(`${API}/sap/connection`).then((r) => {
      if (r.data.configured) {
        setTenantUrl(r.data.tenant_url || "");
        setCommUser(r.data.communication_user || "");
        setHomeState(r.data.home_state_code || "06");
        setMsmeField(r.data.msme_field || "");
        setLedgerFromDate(r.data.ledger_from_date || "2025-04-01");
        setInventoryFromDate(r.data.inventory_from_date || "2026-04-01");
        setIncludeSettled(r.data.include_settled !== false);
        setStatus(r.data.status || "demo");
      }
    }).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/sap/connection`, {
        tenant_url: tenantUrl, communication_user: commUser, communication_password: commPass,
        home_state_code: homeState, msme_field: msmeField,
        ledger_from_date: ledgerFromDate, include_settled: includeSettled,
        inventory_from_date: inventoryFromDate,
      });
      toast.success("SAP connection details saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/sap/test-connection`);
      setStatus(data.status);
      setTestMessage(data.message);
      if (data.status === "connected") toast.success(data.message);
      else toast.info(data.message);
      loadLogs();
    } catch (err) {
      toast.error("Test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="sap-settings-page">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">SAP Connection</h1>
        <p className="text-sm text-slate-500 mt-1">Connect your SAP S/4HANA Public Cloud tenant via OData communication arrangements</p>
      </div>

      <Card className="border-slate-200 shadow-sm fade-in-up">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-800 flex items-center justify-between">
            Tenant Credentials
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${status === "connected" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : status === "error" ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-700"}`} data-testid="sap-connection-status">
              <span className={`h-1.5 w-1.5 rounded-full ${status === "connected" ? "bg-emerald-500" : status === "error" ? "bg-red-500" : "bg-amber-500"}`} />
              {status === "connected" ? "Connected (Live)" : status === "error" ? "Connection Error" : "Demo Mode"}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="tenant">Tenant URL</Label>
              <Input id="tenant" data-testid="sap-tenant-url-input" value={tenantUrl} onChange={(e) => setTenantUrl(e.target.value)}
                     placeholder="https://myXXXXXX.s4hana.cloud.sap" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="commuser">Communication User</Label>
              <Input id="commuser" data-testid="sap-comm-user-input" value={commUser} onChange={(e) => setCommUser(e.target.value)}
                     placeholder="CC_INBOUND_USER" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="commpass">Communication Password</Label>
              <Input id="commpass" data-testid="sap-comm-pass-input" type="password" value={commPass} onChange={(e) => setCommPass(e.target.value)}
                     placeholder={status === "demo" ? "Enter SAP communication user password" : "Leave blank to keep the saved password"} />
              <p className="text-xs text-slate-400">If SAP returns a 401 / "logon failed", the communication user's password has likely expired, been locked, or been regenerated in SAP. Reset it in the SAP "Maintain Communication Users" app, then paste the new password here and Save.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="homestate">Home GST State Code</Label>
              <Input id="homestate" data-testid="sap-home-state-input" value={homeState} onChange={(e) => setHomeState(e.target.value)}
                     placeholder="06" maxLength={2} className="w-24" />
              <p className="text-xs text-slate-400">Your company's GST state code (06 = Haryana). Used to classify supplier invoices as IGST (interstate) vs CGST+SGST (intrastate).</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="msmefield">MSME Identification Type <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input id="msmefield" data-testid="sap-msme-field-input" value={msmeField} onChange={(e) => setMsmeField(e.target.value)}
                     placeholder="e.g. NLOIN1" className="w-72" />
              <p className="text-xs text-slate-400">Business Partner Identification Type code (BPIdentificationType) that holds the MSME/Udyam number, e.g. <span className="font-mono">NLOIN1</span>. A supplier is flagged MSME = Yes when it has an identification of this type with a non-empty number. Leave blank to auto-detect any BP field named MSME/Udyam instead.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ledgerfrom">Ledger From Date <span className="text-slate-400 font-normal">(cleared items)</span></Label>
              <Input id="ledgerfrom" data-testid="sap-ledger-from-input" type="date" value={ledgerFromDate} onChange={(e) => setLedgerFromDate(e.target.value)}
                     className="w-48" />
              <p className="text-xs text-slate-400">Statement of Account opening date. We also pull fully-settled (cleared/zero-balance) documents posted on/after this date so the AP/AR Ledger shows the complete transaction history. Ageing still shows open items only.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="invfrom">Inventory From Date <span className="text-slate-400 font-normal">(stock movements)</span></Label>
              <Input id="invfrom" data-testid="sap-inventory-from-input" type="date" min="2026-04-01" value={inventoryFromDate} onChange={(e) => setInventoryFromDate(e.target.value)}
                     className="w-48" />
              <p className="text-xs text-slate-400">Earliest posting date synced for the Inventory Stock Reconciliation (MB51/MB5B). Capped at 01 Apr 2026 to keep the sync light on high-volume tenants.</p>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-slate-200 p-3">
              <input type="checkbox" id="inclsettled" data-testid="sap-include-settled-toggle" checked={includeSettled}
                     onChange={(e) => setIncludeSettled(e.target.checked)} className="mt-1 h-4 w-4 accent-slate-800" />
              <label htmlFor="inclsettled" className="text-sm text-slate-700">
                <span className="font-medium">Include settled items in the Ledger</span>
                <span className="block text-xs text-slate-400">Pulls fully-paid/cleared documents so the Ledger shows complete history. This is a much heavier extraction. If your sync stalls or times out, turn this OFF — the core open-item ageing (all vendors) will still sync reliably.</span>
              </label>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" data-testid="sap-save-button" disabled={saving} className="bg-sky-700 hover:bg-sky-800 active:scale-[0.98] transition-transform duration-200">
                <ShieldCheck className="h-4 w-4 mr-2" />
                {saving ? "Saving..." : "Save Connection"}
              </Button>
              <Button type="button" variant="outline" data-testid="sap-test-button" onClick={test} disabled={testing} className="border-slate-300">
                <PlugZap className="h-4 w-4 mr-2" />
                {testing ? "Testing..." : "Test Connection"}
              </Button>
            </div>
          </form>
          {testMessage && <p className="text-xs text-slate-500 mt-4 bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="sap-test-message">{testMessage}</p>}
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm fade-in-up">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-800">Required OData APIs</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-600 space-y-2">
          <p>Create a communication arrangement in your SAP tenant exposing these services:</p>
          <ul className="list-disc pl-5 space-y-1 text-slate-500 text-xs font-mono">
            <li>API_OPLACCTGDOCITEMCUBE_SRV (SAP_COM_0303) — open items, WBS, Special GL</li>
            <li>API_BUSINESS_PARTNER — supplier MSME flag, GSTIN, beneficiary bank</li>
            <li>API_SUPPLIERINVOICE_PROCESS_SRV — supplier invoices + tax lines (AP)</li>
            <li>API_BILLING_DOCUMENT_SRV — customer billing (AR)</li>
            <li>API_PURCHASEORDER_PROCESS_SRV — PO account assignment (WBS fallback)</li>
          </ul>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 mt-2">
            The live connector is fully wired: once your credentials pass "Test Connection", every sync (manual or nightly) pulls live OData. If SAP is unreachable, the app auto-falls back to demo data and logs the error below.
          </p>
        </CardContent>
      </Card>

      <FinancierSettings />

      <Card className="border-slate-200 shadow-sm fade-in-up">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold text-slate-800">Sync Log</CardTitle>
              <p className="text-xs text-slate-500 font-normal">
                {syncRunning ? "A sync is running now — this updates automatically when it finishes." : "Manual sync only (nightly auto-sync is off) · last 20 runs"}
              </p>
            </div>
            <Button type="button" variant="outline" size="sm" data-testid="refresh-log-button" onClick={loadLogs} className="border-slate-300">
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-slate-400" data-testid="sync-log-empty">No syncs recorded yet. Trigger one with the "Sync SAP Data" button.</p>
          ) : (
            <div className="space-y-2" data-testid="sync-log-list">
              {logs.map((l) => (
                <div key={l.id} className="border border-slate-200 rounded-md px-3 py-2">
                  <div className="flex items-center gap-3 text-xs">
                    <span className={`h-2 w-2 rounded-full shrink-0 ${l.status === "success" && l.source === "sap_live" ? "bg-emerald-500" : l.status === "fallback_demo" ? "bg-red-500" : "bg-amber-500"}`} />
                    <span className="text-slate-600 w-36 shrink-0">{new Date(l.timestamp).toLocaleString()}</span>
                    <span className="font-medium text-slate-700 w-24 shrink-0 capitalize">{String(l.trigger || "").replace(/_/g, " ")}</span>
                    <span className="text-slate-500 w-32 shrink-0">
                      {l.status === "interrupted" ? "Interrupted" : l.source === "sap_live" ? (l.source_api === "journal_entry_items" ? "Live · JE Items" : "Live · Invoice APIs") : "Demo"}
                    </span>
                    <span className="text-slate-500 shrink-0">{l.ap_count} AP · {l.ar_count} AR · {l.gstr2_count} GST{l.sales_sync ? ` · ${l.sales_sync.rows} sales · ${l.sales_sync.cogs_rows} COGS` : ""}</span>
                    {l.error && <span className={`${l.status === "interrupted" ? "text-amber-700" : "text-red-600"} truncate`} title={l.error} data-testid={`sync-log-error-${l.id}`}>{l.error}</span>}
                  </div>
                  {(l.warnings || []).map((w, wi) => (
                    <p key={wi} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1.5">{w}</p>
                  ))}
                  {l.debug && Object.keys(l.debug).length > 0 && (
                    <details className="mt-1.5" data-testid={`sync-debug-${l.id}`}>
                      <summary className="text-xs text-sky-700 cursor-pointer select-none">Raw SAP field sample (for mapping)</summary>
                      <pre className="text-[10px] text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 mt-1 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(l.debug, null, 2)}</pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
